import React, { useState, useMemo } from "react";
import { List, Avatar, Badge, Typography, Space, Input } from "antd";
import { UserOutlined, MailOutlined, SearchOutlined } from "@ant-design/icons";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

const { Text, Paragraph } = Typography;

export interface Thread {
  guest_uid: number;
  unread_count: number;
  guest_info?: {
    uid: number;
    firstname: string;
    lastname: string;
    email: string;
    full_name: string;
  };
  lastMessage?: {
    id: number;
    subject: string;
    body_preview: string;
    received_at: number;
    direction: "incoming" | "outgoing";
    email_from: string;
    email_to: string;
    is_read: boolean;
  };
}

interface ConversationListProps {
  threads: Thread[];
  selectedGuestUid?: number;
  onThreadSelect: (guestUid: number) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  threads,
  selectedGuestUid,
  onThreadSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return formatDistanceToNow(date, { locale: de, addSuffix: true });
  };

  const getGuestDisplayName = (thread: Thread) => {
    if (thread.guest_info) {
      const { lastname, firstname } = thread.guest_info;
      if (lastname || firstname) {
        return `${lastname}${lastname && firstname ? ', ' : ''}${firstname}`.trim();
      }
      return thread.guest_info.email || "Unbekannter Gast";
    }
    // Fallback to email parsing if no guest_info
    if (thread.lastMessage?.email_from) {
      const email = thread.lastMessage.email_from;
      const name = email.split("@")[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return "Unbekannter Gast";
  };

  const getGuestEmail = (thread: Thread) => {
    if (thread.guest_info?.email) {
      return thread.guest_info.email;
    }
    return thread.lastMessage?.email_from || "";
  };

  const getInitials = (thread: Thread) => {
    if (thread.guest_info) {
      const { firstname, lastname } = thread.guest_info;
      if (firstname && lastname) {
        return firstname.charAt(0).toUpperCase() + lastname.charAt(0).toUpperCase();
      }
      if (lastname) {
        return lastname.substring(0, 2).toUpperCase();
      }
      if (firstname) {
        return firstname.substring(0, 2).toUpperCase();
      }
    }
    // Fallback to email
    const email = getGuestEmail(thread);
    if (email) {
      const parts = email.split("@")[0].split(".");
      if (parts.length >= 2) {
        return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase();
      }
      return email.charAt(0).toUpperCase();
    }
    return "?";
  };

  // Filter threads based on search term
  const filteredThreads = useMemo(() => {
    if (!searchTerm) return threads;
    
    const searchLower = searchTerm.toLowerCase();
    return threads.filter(thread => {
      const displayName = getGuestDisplayName(thread).toLowerCase();
      const email = getGuestEmail(thread).toLowerCase();
      const subject = thread.lastMessage?.subject?.toLowerCase() || "";
      const preview = thread.lastMessage?.body_preview?.toLowerCase() || "";
      
      return displayName.includes(searchLower) || 
             email.includes(searchLower) || 
             subject.includes(searchLower) || 
             preview.includes(searchLower);
    });
  }, [threads, searchTerm]);

  // Check if thread has new messages
  const hasNewMessages = (thread: Thread) => {
    return thread.unread_count > 0 && thread.lastMessage?.direction === "incoming";
  };

  return (
    <div className="conversation-list">
      <div className="conversation-header">
        <Space align="center">
          <MailOutlined />
          <span>Konversationen</span>
        </Space>
        <Badge count={threads.reduce((sum, t) => sum + t.unread_count, 0)} />
      </div>
      
      <div className="conversation-search">
        <Input
          placeholder="Konversationen durchsuchen..."
          prefix={<SearchOutlined style={{ color: '#72767d' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
      </div>

      <div className="thread-list">
        <List
          dataSource={filteredThreads}
          renderItem={(thread) => (
            <List.Item
              className={`thread-item ${selectedGuestUid === thread.guest_uid ? "selected" : ""} ${hasNewMessages(thread) ? "has-new" : ""}`}
              onClick={() => onThreadSelect(thread.guest_uid)}
            >
              <List.Item.Meta
                avatar={
                  <Badge count={thread.unread_count} offset={[-2, 2]}>
                    <Avatar size={48} icon={<UserOutlined />}>
                      {getInitials(thread)}
                    </Avatar>
                  </Badge>
                }
                title={
                  <div className="thread-title">
                    <div className="guest-info">
                      <Text strong className="guest-name">
                        {getGuestDisplayName(thread)}
                      </Text>
                      <Text className="guest-email" type="secondary">
                        {getGuestEmail(thread)}
                      </Text>
                    </div>
                    <div className="thread-meta">
                      {hasNewMessages(thread) && <span className="new-indicator">NEU</span>}
                      <Text type="secondary" className="thread-time">
                        {thread.lastMessage ? formatTime(thread.lastMessage.received_at) : ""}
                      </Text>
                    </div>
                  </div>
                }
                description={
                  thread.lastMessage ? (
                    <div className="thread-description">
                      <Text strong className="thread-subject">
                        {thread.lastMessage.subject || "Kein Betreff"}
                      </Text>
                      <Paragraph 
                        ellipsis={{ rows: 2 }} 
                        className={`thread-preview ${!thread.lastMessage.is_read ? 'unread' : ''}`}
                        type="secondary"
                      >
                        {thread.lastMessage.body_preview || "Keine Nachricht"}
                      </Paragraph>
                    </div>
                  ) : (
                    <div className="thread-description">
                      <Text type="secondary" className="no-messages">
                        Noch keine Nachrichten
                      </Text>
                    </div>
                  )
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: searchTerm ? "Keine Konversationen gefunden" : "Keine Gäste vorhanden" }}
        />
      </div>
    </div>
  );
}; 